import { createContext, useContext, useMemo, useEffect, useCallback, type ReactNode } from 'react';
import { usePrivy, getAccessToken as privyGetAccessToken } from '@privy-io/react-auth';
import { setSentryWallet } from '@/main';
import { identifyWallet, resetIdentity, trackEvent, Events } from '@/lib/analytics';
import { clearWalletState } from '@/lib/wallet-state';

/**
 * Privy-backed auth.
 *
 * The exported shape matches the previous SIWE hook so call sites
 * (useAccess, wallet-button, marketing portal, etc.) keep working
 * without changes. Differences vs SIWE:
 *   - signIn() opens Privy's login modal (wallet-only, configured in App.tsx)
 *   - authenticatedAddress comes from user.wallet.address
 *   - No cross-site cookies; downstream API calls attach a Bearer JWT
 *     fetched via getAccessToken()
 */

interface AuthState {
  isAuthenticated: boolean;
  authenticatedAddress: string | null;
  isSigningIn: boolean;
  sessionError: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  authenticatedAddress: null,
  isSigningIn: false,
  sessionError: false,
  signIn: async () => {},
  signOut: async () => {},
  error: null,
});

export function useAuth() {
  return useContext(AuthContext);
}

/** Fetch the current Privy access token for use in API Authorization headers. */
export async function getAccessToken(): Promise<string | null> {
  try {
    return await privyGetAccessToken();
  } catch {
    return null;
  }
}

/**
 * Module-level cache of the currently authenticated wallet so non-React
 * callers (API helpers, analytics, etc.) can read it synchronously.
 * `AuthProvider` keeps this in sync via a `useEffect`. Privy doesn't always
 * populate `window.ethereum` (embedded wallets, certain injected providers),
 * so this is more reliable than sniffing `window.ethereum.selectedAddress`.
 */
let currentWallet: string | null = null;
export function getAuthenticatedWallet(): string | null {
  return currentWallet;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { ready, authenticated, user, login, logout } = usePrivy();

  const authenticatedAddress = useMemo<string | null>(() => {
    if (!authenticated || !user?.wallet?.address) return null;
    return user.wallet.address.toLowerCase();
  }, [authenticated, user]);

  // Populate the module cache synchronously during render so child components'
  // effects (which fire before parent effects) can read the wallet on their
  // very first API call. Without this, queries that fire on mount race with
  // `useEffect` and send requests without the X-Wallet-Address header.
  // Writing to a module-level variable during render is safe here because it's
  // an idempotent mirror of Privy state, not React state.
  currentWallet = authenticatedAddress;

  // Sentry + analytics side effects can stay in an effect.
  useEffect(() => {
    setSentryWallet(authenticatedAddress);
    if (authenticatedAddress) {
      identifyWallet(authenticatedAddress);
      // Fire Wallet Connected when Privy auth completes (address transitions null → value).
      // This is the correct place — fires once per login, after Privy confirms the wallet.
      trackEvent(Events.WALLET_CONNECTED, { provider: 'privy' });
      // Distinguish first-time signup from a returning connect (per-wallet, this device).
      try {
        const key = `os_signup_seen_${authenticatedAddress.toLowerCase()}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1');
          trackEvent(Events.SIGN_IN, { provider: 'privy', firstTime: true });
        }
      } catch {
        // localStorage unavailable — skip the signup marker, non-critical.
      }
    }
  }, [authenticatedAddress]);

  const signIn = useCallback(async () => {
    try {
      login();
    } catch (e) {
      console.warn('[auth] Privy login failed', e);
    }
  }, [login]);

  const signOut = useCallback(async () => {
    // Clear wallet-scoped localStorage before Privy tears down the session so
    // stale subscription status, yield data, etc. don't leak to the next wallet.
    if (authenticatedAddress) clearWalletState(authenticatedAddress);
    try {
      await logout();
    } catch {
      // ignore
    }
    trackEvent(Events.WALLET_DISCONNECTED, { provider: 'privy' });
    resetIdentity();
  }, [logout, authenticatedAddress]);

  const value: AuthState = {
    isAuthenticated: !!authenticatedAddress,
    authenticatedAddress,
    // Treat the pre-ready window as "signing in" so consumers show a spinner
    // instead of flashing the disconnected state.
    isSigningIn: !ready,
    sessionError: false,
    signIn,
    signOut,
    error: null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
